import { Injectable } from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  UntypedFormArray,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import {
  Parameters,
  PledgeProductRead,
} from '@app/forms/subscription/definition/interface/Pledge';
import { FormConfig } from '@app/forms/subscription/definition/types/subscription';
import FormGroupArray from '@app/forms/subscription/services/FormGroupArray';
import * as _ from 'lodash';

function clamp(n: number, min: number, max: number): number {
  const v = Number(n);
  if (isNaN(v)) return min;
  return Math.max(min, Math.min(max, v));
}
function roundTo(n: number, digits: number): number {
  const v = Number(n);
  if (isNaN(v)) return 0;
  return Number(v.toFixed(digits));
}
function maxFractionDigits(maxDigits: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;
    const s = String(raw).replace(',', '.');
    if (!/^\d*\.?\d*$/.test(s)) return { number: true };
    const [, decimals] = s.split('.');
    if (decimals && decimals.length > maxDigits) {
      return { maxFractionDigits: { max: maxDigits } };
    }
    return null;
  };
}

@Injectable({ providedIn: 'root' })
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  generateForm(entity: PledgeProductRead & { parameters: any }, option) {
    const parameters: Parameters = { ...option };
    const options: AbstractControlOptions = {};

    const supportsReinvestmentFlux: UntypedFormGroup[] = [];
    const reinvestmentFlux = entity?.parameters?.supportsInvestmentFlux || [];

    if (reinvestmentFlux) {
      _.forEach(reinvestmentFlux, support =>
        supportsReinvestmentFlux.push(
          this.formBuilder.group(this.createModel(support)),
        ),
      );
    }

    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: this.formBuilder.group({
        currentMonthlyPayment: [entity?.parameters?.monthlyPayment || 0],
        arbitrageType: [entity?.parameters?.arbitrageType || null],
        supportsInvestmentFlux: this.formBuilder.array(
          supportsReinvestmentFlux,
          Validators.required,
        ),
      }),
    };

    const productForm = this.formBuilder.group(formConfig, options);

    this.attachReinvestmentFluxSyncProduct(productForm);

    return productForm;
  }

  generate(entity: any, option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
      propertyName: 'products',
    });
  }

  public createModel(item) {
    return {
      id: [item ? item.id : null],
      name: [item ? item.name : null],
      type: [item ? item.type : null],
      isin: [item ? item.isin : null],
      index: [item?.index || null],
      details: [item ? item.details : null],

      repartitionFlux: [
        item && !_.isNil(item.repartitionFlux)
          ? roundTo(item.repartitionFlux * 100, 4)
          : 0,
        [
          Validators.required,
          Validators.min(0),
          Validators.max(100),
          maxFractionDigits(4),
        ],
      ],

      amountRepartitionFlux: [
        item && item.amountRepartitionFlux
          ? roundTo(item.amountRepartitionFlux, 2)
          : 0,
        [Validators.required],
      ],

      repartitionStock: [
        item && item.repartitionStock ? item.repartitionStock : null,
      ],
      amountRepartitionStock: [
        item && item.amountRepartitionStock
          ? item.amountRepartitionStock
          : null,
      ],
      currentAllocationAmountStock: [
        item && item.currentAllocationAmountStock
          ? item.currentAllocationAmountStock
          : null,
      ],

      // PRE-4896 : cette slide n'affiche ni ne modifie ces 8 champs, mais doit les faire transiter
      // tels quels (préremplis par la commande COMPLETE_SUPPORT_RATES au submit de cette slide) —
      // sans cela, l'extract() écraserait leur valeur à null avant même que la slide
      // "tauxParSupportArbitrage" ait pu les afficher.
      upfrontAssureurAutocomplete: [item?.upfrontAssureurAutocomplete ?? null],
      upfrontEmetteurAutocomplete: [item?.upfrontEmetteurAutocomplete ?? null],
      fraisGestionAutocomplete: [item?.fraisGestionAutocomplete ?? null],
      retrocessionAssureurAutocomplete: [
        item?.retrocessionAssureurAutocomplete ?? null,
      ],
      upfrontAssureurSaisi: [item?.upfrontAssureurSaisi ?? null],
      upfrontEmetteurSaisi: [item?.upfrontEmetteurSaisi ?? null],
      fraisGestionSaisi: [item?.fraisGestionSaisi ?? null],
      retrocessionAssureurSaisi: [item?.retrocessionAssureurSaisi ?? null],
    };
  }

  private _syncedFlux = new WeakSet<UntypedFormGroup>();

  private attachReinvestmentFluxSyncProduct(product: UntypedFormGroup) {
    const params = product.get('parameters') as UntypedFormGroup;
    if (!params) return;

    const supportsInvFlux = params.get(
      'supportsInvestmentFlux',
    ) as UntypedFormArray;
    const monthlyCtrl = params.get('currentMonthlyPayment');
    if (!supportsInvFlux) return;

    const total = () => Number(monthlyCtrl?.value) || 0;

    const attachOne = (ctrl: UntypedFormGroup) => {
      if (this._syncedFlux.has(ctrl)) return;
      this._syncedFlux.add(ctrl);

      const percentCtrl = ctrl.get('repartitionFlux');
      const amountCtrl = ctrl.get('amountRepartitionFlux');
      if (!percentCtrl || !amountCtrl) return;

      let syncing = false;

      percentCtrl.valueChanges.subscribe((raw: any) => {
        if (syncing) return;
        syncing = true;

        let p = Number(String(raw).replace(',', '.'));
        if (isNaN(p)) p = 0;
        p = clamp(p, 0, 100);
        const p4 = roundTo(p, 4);
        if (p4 !== Number(raw)) {
          percentCtrl.setValue(p4, { emitEvent: false });
        }

        const t = total();
        const amount = roundTo(t * (p4 / 100), 2);
        amountCtrl.setValue(amount, { emitEvent: false });
        amountCtrl.updateValueAndValidity({ emitEvent: false });

        syncing = false;
      });

      amountCtrl.valueChanges.subscribe((raw: any) => {
        if (syncing) return;
        syncing = true;

        const t = total();
        let amt = Number(String(raw).replace(',', '.'));
        if (isNaN(amt)) amt = 0;
        amt = clamp(amt, 0, t);
        const amt2 = roundTo(amt, 2);
        if (amt2 !== Number(raw)) {
          amountCtrl.setValue(amt2, { emitEvent: false });
        }

        const p = t > 0 ? (amt2 / t) * 100 : 0;
        const p4 = roundTo(clamp(p, 0, 100), 4);
        percentCtrl.setValue(p4, { emitEvent: false });
        percentCtrl.updateValueAndValidity({ emitEvent: false });

        syncing = false;
      });

      const initP = clamp(Number(percentCtrl.value) || 0, 0, 100);
      percentCtrl.setValue(roundTo(initP, 4), { emitEvent: true });
    };

    supportsInvFlux.controls.forEach((c: UntypedFormGroup) => attachOne(c));
    supportsInvFlux.valueChanges.subscribe(() => {
      supportsInvFlux.controls.forEach((c: UntypedFormGroup) => attachOne(c));
    });

    monthlyCtrl?.valueChanges?.subscribe(() => {
      const t = total();
      supportsInvFlux.controls.forEach((ctrl: UntypedFormGroup) => {
        const p = Number(ctrl.get('repartitionFlux')?.value) || 0;
        const amount = roundTo((t * clamp(p, 0, 100)) / 100, 2);
        ctrl
          .get('amountRepartitionFlux')
          ?.setValue(amount, { emitEvent: false });
      });
    });
  }

  get currentMonthlyPayment(): number {
    return (
      this.form
        .get('products')
        .get(`${0}`)
        .get('parameters')
        .get('currentMonthlyPayment').value || 0
    );
  }

  get arbitrageType(): string {
    return this.form
      .get('products')
      .get(`${0}`)
      .get('parameters')
      .get('arbitrageType').value;
  }

  get products() {
    return this.form.get('products') as UntypedFormArray;
  }
  get supportsInvestmentFlux() {
    return this.products
      .get(`${0}`)
      .get('parameters')
      .get('supportsInvestmentFlux') as UntypedFormArray;
  }
  get productId() {
    return this.form.get('products').get(`${0}`).get('productId').value;
  }

  extract() {
    const products = _.cloneDeep(this.form.value.products);
    products.forEach(product => {
      let supportsInvestmentFlux = _.cloneDeep(
        product.parameters.supportsInvestmentFlux,
      );

      supportsInvestmentFlux.forEach(support => {
        support.repartitionFlux = !_.isNil(support.repartitionFlux)
          ? Number(support.repartitionFlux) / 100
          : 0;
      });

      product.parameters.supportsInvestmentFlux = supportsInvestmentFlux;
    });
    return products;
  }
}
